import { useState, useEffect, useMemo, Fragment, useRef } from 'react';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';

// Use unified date utils
import { moment } from '../../utils/dateUtils';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';

import {
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  Select,
  MenuItem,
  Chip,
  Divider,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SendIcon from '@mui/icons-material/Send';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import NotesIcon from '@mui/icons-material/Notes';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PreviewIcon from '@mui/icons-material/Preview';
import EmailIcon from '@mui/icons-material/Email';
import Collapse from '@mui/material/Collapse';

import { Spinner } from '../index';
import messages from '../../static/messages';
import proposalsServices from '../../services/proposalsServices';
import suppliersServices from '../../services/suppliersServices';
import equipmentModelsServices from '../../services/equipmentModelsServices';
import { modal_style } from '../../static/styles';
import {
  isSupplierRecommended,
  buildOrderEmailText,
  emailTextToHtml,
  computeAutoSupplies,
  collectSentModels,
} from '../../utils/equipmentOrderUtils';
import supplyRulesServices from '../../services/supplyRulesServices';

const OrderEquipmentModal = ({ open, onClose, systems, orders, publicId, proposalId, refresh, additionalNotes, onSaveNotes, installationSchedule, createdBy, proposal }) => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [allOrdered, setAllOrdered] = useState(additionalNotes?.orderEquipmentCompleted?.completed || false);
  
  // Equipment items with qty and supplier selection
  const [orderItems, setOrderItems] = useState([]);
  
  // Special instructions per supplier
  const [supplierInstructions, setSupplierInstructions] = useState({});
  
  // Custom emails
  const [customEmails, setCustomEmails] = useState('');
  
  // Delivery date
  const [deliveryDate, setDeliveryDate] = useState(null);
  
  // Expanded rows for item instructions
  const [expandedRows, setExpandedRows] = useState({});
  
  // Review mode state
  const [showReview, setShowReview] = useState(false);
  const [emailTexts, setEmailTexts] = useState({});
  
  // Catalog suppliers map: modelNumber -> suppliers string
  const [catalogSuppliers, setCatalogSuppliers] = useState({});

  // Supplier-vs-catalog mismatch warning state
  const [mismatchDialogOpen, setMismatchDialogOpen] = useState(false);
  const [mismatchAck, setMismatchAck] = useState(false);
  
  // Track if items were initialized
  const isInitialized = useRef(false);

  // Auto-supplies catalog rules (Settings → Auto-Supplies)
  const [supplyRules, setSupplyRules] = useState([]);
  const suppliesAdded = useRef(false);

  // When the client accepted one of the proposal options, only the systems
  // of that option are part of the contract — equipment from the declined
  // options must not be ordered.
  const activeSystems = useMemo(() => {
    if (proposal?.optionAccepted && proposal?.selectedOptionId) {
      return (systems || []).filter(s => s.optionId === proposal.selectedOptionId);
    }
    return systems || [];
  }, [systems, proposal]);

  // Extract equipment from proposalSystems
  // New structure: proposalSystems[].equipments[] with denormalized fields
  const extractEquipment = useMemo(() => {
    const equipment = [];
    const seen = new Set();
    
    activeSystems?.forEach((system) => {
      const systemEquipments = system.equipments || [];
      
      systemEquipments.forEach((eq) => {
        // Data is denormalized directly in the equipment record
        const key = eq.id;
        
        if (!seen.has(key) && eq.id) {
          seen.add(key);
          equipment.push({
            id: eq.id,
            // Use denormalized fields from proposal_system_equipment
            type: eq.equipmentType || '-',
            make: eq.manufacturerName || '-',
            model: eq.modelNumber || '-',
            name: eq.equipmentName || '-',
            price: eq.priceAtCreation || 0,
            qty: 1,
            supplierId: null,
            supplierName: '',
            instruction: '',
          });
        }
      });
    });
    
    return equipment;
  }, [activeSystems]);

  // Get unique selected suppliers
  const selectedSuppliers = useMemo(() => {
    const supplierIds = new Set();
    const result = [];
    
    orderItems.forEach(item => {
      if (item.supplierId && !supplierIds.has(item.supplierId)) {
        supplierIds.add(item.supplierId);
        const supplier = suppliers.find(s => s.id === item.supplierId);
        if (supplier) {
          result.push(supplier);
        }
      }
    });
    
    // Add "Other" if any item has "other" as supplier
    if (orderItems.some(item => item.supplierId === 'other')) {
      result.push({ id: 'other', name: 'Other', email: '' });
    }
    
    return result;
  }, [orderItems, suppliers]);

  // Check if order is valid
  const isOrderValid = useMemo(() => {
    const hasItemsWithSupplier = orderItems.some(item => item.supplierId && item.qty > 0);
    const otherSupplier = selectedSuppliers.find(s => s.id === 'other');
    if (otherSupplier && !customEmails.trim()) {
      return false;
    }
    return hasItemsWithSupplier;
  }, [orderItems, selectedSuppliers, customEmails]);

  // Check if all equipment from the proposal is covered by sent orders
  const allEquipmentCovered = useMemo(() => {
    if (!extractEquipment.length || !orders?.length) return false;

    // Only consider equipment with a real model number
    const proposalModels = extractEquipment
      .map(eq => eq.model)
      .filter(m => m && m !== '-');
    if (proposalModels.length === 0) return false;

    // Collect all models from SENT orders (sentBy means email was actually sent)
    const orderedModels = new Set();
    orders.filter(o => o.sentBy).forEach(order => {
      order.items?.forEach(item => {
        if (item.model && item.model !== '-') orderedModels.add(item.model);
      });
    });

    return proposalModels.every(model => orderedModels.has(model));
  }, [extractEquipment, orders]);

  // Items whose chosen supplier is not among the catalog-recommended suppliers
  // for that model — the equipment may not be available there.
  const supplierMismatches = useMemo(() => {
    return orderItems
      .filter(item => item.supplierId && item.supplierId !== 'other' && item.qty > 0)
      .map(item => {
        const recommended = catalogSuppliers[item.model];
        const supplier = suppliers.find(s => s.id === item.supplierId);
        const supplierName = supplier?.name || item.supplierName || '';
        if (recommended && !isSupplierRecommended(supplierName, recommended)) {
          return { id: item.id, model: item.model, make: item.make, selected: supplierName, recommended };
        }
        return null;
      })
      .filter(Boolean);
  }, [orderItems, catalogSuppliers, suppliers]);

  // Load suppliers and initialize items when modal opens
  useEffect(() => {
    if (open) {
      loadSuppliers();
      loadCatalogSuppliers();
      loadSupplyRules();
      const savedAllOrdered = additionalNotes?.orderEquipmentCompleted?.completed || false;

      // Auto-check if all equipment is covered by sent orders but not yet marked
      if (!savedAllOrdered && allEquipmentCovered) {
        setAllOrdered(true);
        if (onSaveNotes) {
          onSaveNotes({
            ...additionalNotes,
            orderEquipmentCompleted: { completed: true }
          });
        }
      } else {
        setAllOrdered(savedAllOrdered);
      }
      
      // Initialize order items only once per modal open
      if (!isInitialized.current && extractEquipment.length > 0) {
        setOrderItems(extractEquipment);
        isInitialized.current = true;
      }
      
      // Set delivery date from installation schedule if available
      if (installationSchedule?.length > 0) {
        const firstInstallDate = installationSchedule[0]?.date;
        if (firstInstallDate) {
          // Parse date as local date (ignore timezone) to avoid day shift
          const dateOnly = firstInstallDate.split('T')[0]; // "2025-12-18"
          setDeliveryDate(dayjs(dateOnly));
        }
      }
    } else {
      // Reset initialization flags when modal closes
      isInitialized.current = false;
      suppliesAdded.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Append supply rows suggested by the auto-supplies rules once equipment
  // rows, suppliers and rules are all loaded. Supplies apply only to
  // equipment that has not been sent in a previous order; the user can zero
  // the qty or change the supplier like for any other row.
  useEffect(() => {
    if (!open || suppliesAdded.current) return;
    if (!isInitialized.current || suppliers.length === 0 || supplyRules.length === 0) return;

    const sentModels = collectSentModels(orders);
    let pendingEquipment = extractEquipment.filter(
      (eq) => eq.model && eq.model !== '-' && !sentModels.has(eq.model)
    );
    // When no option is accepted the systems are alternatives of the same
    // job — the same model repeated across options must count once for
    // supplies (the office zeroes the duplicate equipment rows, but supplies
    // used to be computed from the full list and came out doubled).
    if (!(proposal?.optionAccepted && proposal?.selectedOptionId)) {
      const seenModels = new Set();
      pendingEquipment = pendingEquipment.filter((eq) => {
        const key = (eq.model || '').toUpperCase();
        if (seenModels.has(key)) return false;
        seenModels.add(key);
        return true;
      });
    }
    const supplies = computeAutoSupplies({
      equipment: pendingEquipment,
      rules: supplyRules,
      suppliers,
      fallbackSupplier: null,
    });
    suppliesAdded.current = true;
    if (supplies.length === 0) return;

    setOrderItems((prev) => {
      const existingModels = new Set(prev.map((i) => (i.model || '').toUpperCase()));
      const rows = supplies
        .filter((s) => !existingModels.has(s.item.model.toUpperCase()))
        .map((s, idx) => ({
          id: `supply-${idx}`,
          type: s.item.type,
          make: s.item.make || '-',
          model: s.item.model,
          name: s.ruleName || 'Auto-supply',
          price: 0,
          qty: s.item.qty,
          supplierId: s.supplier?.id || null,
          supplierName: s.supplier?.name || '',
          instruction: '',
          isSupply: true,
        }));
      return rows.length ? [...prev, ...rows] : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, suppliers, supplyRules, orders, extractEquipment]);

  const loadSuppliers = async () => {
    setLoading(true);
    try {
      const response = await suppliersServices.getSuppliers();
      if (response.status === 200) {
        setSuppliers(response.data.value?.data || response.data?.data || []);
      }
    } catch (error) {
      console.error(error);
      toast.error(messages.errors.error_data_loaning);
    } finally {
      setLoading(false);
    }
  };

  const loadCatalogSuppliers = async () => {
    try {
      const response = await equipmentModelsServices.getAll({ skip: 0, take: 10000 });
      const models = response.data?.data || [];
      const map = {};
      models.forEach((m) => {
        if (m.suppliers) map[m.name] = m.suppliers;
      });
      setCatalogSuppliers(map);
    } catch (error) {
      console.error('Failed to load catalog suppliers:', error);
    }
  };

  const loadSupplyRules = async () => {
    try {
      const response = await supplyRulesServices.getAll();
      setSupplyRules((response.data || []).filter((r) => r.active !== false));
    } catch (error) {
      console.error('Failed to load supply rules:', error);
    }
  };

  const handleQtyChange = (itemId, qty) => {
    setOrderItems(prev => prev.map(item => 
      item.id === itemId ? { ...item, qty: Math.max(0, parseInt(qty) || 0) } : item
    ));
  };

  const handleSupplierChange = (itemId, supplierId) => {
    const supplier = suppliers.find(s => s.id === supplierId);
    setOrderItems(prev => prev.map(item => 
      item.id === itemId ? { 
        ...item, 
        supplierId, 
        supplierName: supplierId === 'other' ? 'Other' : (supplier?.name || '')
      } : item
    ));
    // Any supplier change re-arms the mismatch confirmation.
    setMismatchAck(false);
  };

  const handleInstructionChange = (supplierId, value) => {
    setSupplierInstructions(prev => ({
      ...prev,
      [supplierId]: value
    }));
  };

  const handleItemInstructionChange = (itemId, value) => {
    setOrderItems(prev => prev.map(item => 
      item.id === itemId ? { ...item, instruction: value } : item
    ));
  };

  const toggleRowExpanded = (itemId) => {
    setExpandedRows(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const handleAllOrderedChange = async (event) => {
    const completed = event.target.checked;
    setAllOrdered(completed);
    
    if (onSaveNotes) {
      onSaveNotes({
        ...additionalNotes,
        orderEquipmentCompleted: { completed }
      });
    }
  };

  // Generate email text for a supplier (shared builder — includes the
  // Will Call / Prepull block when the job is today or tomorrow)
  const generateEmailText = (supplierId) => {
    const supplierItems = orderItems.filter(i => i.supplierId === supplierId && i.qty > 0);
    const supplier = suppliers.find(s => s.id === supplierId);
    const supplierName = supplierId === 'other' ? 'Supplier' : (supplier?.name || 'Supplier');

    return buildOrderEmailText({
      supplierName,
      items: supplierItems.filter(i => !i.isSupply),
      supplies: supplierItems.filter(i => i.isSupply),
      publicId,
      deliveryDate,
      createdBy,
      supplierInstruction: supplierInstructions[supplierId] || '',
    });
  };

  // Build review email texts and move to the review screen.
  const proceedToReview = () => {
    const texts = {};
    selectedSuppliers.forEach(supplier => {
      texts[supplier.id] = generateEmailText(supplier.id);
    });
    setEmailTexts(texts);
    setShowReview(true);
  };

  // Handle Review Order click — interrupt with a warning when the chosen
  // supplier does not match the catalog recommendation for the model.
  const handleReviewOrder = () => {
    if (supplierMismatches.length > 0 && !mismatchAck) {
      setMismatchDialogOpen(true);
      return;
    }
    proceedToReview();
  };

  const handleConfirmMismatch = () => {
    setMismatchAck(true);
    setMismatchDialogOpen(false);
    proceedToReview();
  };

  // Handle email text change
  const handleEmailTextChange = (supplierId, value) => {
    setEmailTexts(prev => ({
      ...prev,
      [supplierId]: value
    }));
  };

  // Handle back from review
  const handleBackFromReview = () => {
    setShowReview(false);
  };

  const handlePlaceOrder = async () => {
    setSending(true);
    
    try {
      // Group items by supplier
      const ordersBySupplier = {};
      
      orderItems.forEach(item => {
        if (item.supplierId && item.qty > 0) {
          if (!ordersBySupplier[item.supplierId]) {
            ordersBySupplier[item.supplierId] = {
              supplierId: item.supplierId,
              supplierName: item.supplierName,
              items: [],
              instruction: supplierInstructions[item.supplierId] || '',
              emailText: emailTexts[item.supplierId] || ''
            };
          }
          ordersBySupplier[item.supplierId].items.push({
            id: item.id,
            type: item.type,
            make: item.make,
            model: item.model,
            qty: item.qty,
            instruction: item.instruction || ''
          });
        }
      });

      const deliveryDateFormatted = deliveryDate ? dayjs(deliveryDate).format('MM/DD/YYYY') : null;

      // Build new orders for all suppliers
      const newOrders = [];
      const emailData = []; // Store email data for sending after creation

      for (const supplierOrder of Object.values(ordersBySupplier)) {
        const supplierId = supplierOrder.supplierId;
        let emailsToSend = [];
        
        if (supplierId === 'other') {
          customEmails.split(',').forEach(email => {
            if (email.trim()) emailsToSend.push(email.trim());
          });
        } else {
          const supplier = suppliers.find(s => s.id === supplierId);
          // Support new emails array format
          if (supplier?.emails && Array.isArray(supplier.emails)) {
            emailsToSend.push(...supplier.emails);
          } else if (supplier?.email) {
            // Legacy support for old single email format
            emailsToSend.push(supplier.email);
          }
        }

        // Convert plain text to HTML for email
        const plainText = supplierOrder.emailText || emailTexts[supplierId] || '';
        const htmlContent = emailTextToHtml(plainText);

        // Create order record for this supplier with detailed equipment info
        const newOrder = {
          items: supplierOrder.items.map(item => ({
            type: item.type,
            make: item.make,
            model: item.model,
            qty: item.qty,
            instruction: item.instruction || ''
          })),
          emails: emailsToSend,
          instruction: supplierOrder.instruction || '',
          content: htmlContent
        };

        newOrders.push(newOrder);
        emailData.push({ htmlContent, emailsToSend });
      }

      // Save all orders at once
      const response = await proposalsServices.update(proposalId, {
        equipmentOrders: [...orders, ...newOrders]
      });

      if (response.status === 200) {
        // Get the newly created orders from response
        const allOrders = response.data?.equipmentOrders || [];
        // Existing order IDs (before this batch)
        const existingIds = new Set(orders.map(o => o.id).filter(Boolean));
        // New orders = those not in existing set
        const createdOrders = allOrders.filter(o => !existingIds.has(o.id));

        // Match each new order to its emailData by comparing emails arrays
        for (const savedOrder of createdOrders) {
          const savedEmails = (savedOrder.emails || []).map(e => e.toLowerCase()).sort().join(',');
          const matchIdx = emailData.findIndex(ed => {
            const edEmails = (ed.emailsToSend || []).map(e => e.toLowerCase()).sort().join(',');
            return edEmails === savedEmails;
          });
          
          if (matchIdx !== -1 && savedOrder.id) {
            const { emailsToSend } = emailData[matchIdx];
            if (emailsToSend.length > 0) {
              try {
                await proposalsServices.sendEquipmentOrder(savedOrder.id, savedOrder.content, createdBy?.email);
              } catch (emailError) {
                console.error('Failed to send email for order:', savedOrder.id, emailError);
              }
            }
            // Remove matched entry to prevent duplicate matching
            emailData.splice(matchIdx, 1);
          }
        }
        
        // Check if all equipment is now covered (existing sent + just placed)
        const allOrderedModels = new Set();
        // From existing sent orders
        orders.filter(o => o.sentBy).forEach(order => {
          order.items?.forEach(item => {
            if (item.model && item.model !== '-') allOrderedModels.add(item.model);
          });
        });
        // From just-placed orders (emails were just sent)
        Object.values(ordersBySupplier).forEach(so => {
          so.items?.forEach(item => {
            if (item.model && item.model !== '-') allOrderedModels.add(item.model);
          });
        });

        const proposalModels = extractEquipment
          .map(eq => eq.model)
          .filter(m => m && m !== '-');

        const nowAllCovered = proposalModels.length > 0 &&
          proposalModels.every(model => allOrderedModels.has(model));

        if (nowAllCovered && !allOrdered) {
          setAllOrdered(true);
          if (onSaveNotes) {
            onSaveNotes({
              ...additionalNotes,
              orderEquipmentCompleted: { completed: true }
            });
          }
        }

        toast.success('Order placed and email sent!');
        refresh();
        handleClose();
      }
    } catch (error) {
      console.error(error);
      toast.error(messages.errors.error_try_again);
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    setOrderItems([]);
    setSupplierInstructions({});
    setCustomEmails('');
    setDeliveryDate(null);
    setExpandedRows({});
    setShowReview(false);
    setEmailTexts({});
    setCatalogSuppliers({});
    setMismatchDialogOpen(false);
    setMismatchAck(false);
    onClose();
  };

  if (!open) return null;

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={{ 
        ...modal_style, 
        width: '90%', 
        maxWidth: 900,
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocalShippingIcon color="primary" />
            <strong>Order Equipment</strong>
            <Chip label={`PO: ${publicId}`} size="small" color="primary" variant="outlined" />
          </Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Status checkbox */}
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <FormControlLabel
            control={
              <Checkbox 
                checked={allOrdered} 
                onChange={handleAllOrderedChange}
                color="success"
              />
            }
            label={
              <Typography sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {allOrdered && <CheckCircleIcon color="success" fontSize="small" />}
                All equipment has been ordered
              </Typography>
            }
          />
        </Box>

        {/* Delivery Date */}
        <Box sx={{ mb: 2 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DesktopDatePicker
              label="Job is scheduled for"
              value={deliveryDate}
              onChange={(newValue) => setDeliveryDate(newValue)}
              minDate={dayjs()}
              inputFormat="MM/DD/YYYY"
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  sx={{ width: 250 }}
                />
              )}
            />
          </LocalizationProvider>
        </Box>

        {/* Order History */}
        {orders?.length > 0 && (
          <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
            <Typography variant="subtitle2" gutterBottom>
              <strong>Previous Orders:</strong>
            </Typography>
            {orders.filter(o => o.sentBy).map((order, idx) => (
              <Accordion 
                key={order.id || idx} 
                sx={{ 
                  mb: 1, 
                  bgcolor: 'white',
                  '&:before': { display: 'none' }
                }}
                elevation={0}
                variant="outlined"
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%' }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {moment(order.createdAt).format('MM/DD/YYYY hh:mm A')}
                      <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                        by {order.sentBy?.fullName || 'Unknown'}
                      </Typography>
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      To: {order.emails?.join(', ')}
                      {order.items?.length > 0 && (
                        <Chip 
                          label={`${order.items.length} item${order.items.length > 1 ? 's' : ''}`} 
                          size="small" 
                          sx={{ ml: 1, height: 18, fontSize: '0.7rem' }}
                        />
                      )}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  {order.items?.length > 0 ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'grey.100' }}>
                            <TableCell><strong>Type</strong></TableCell>
                            <TableCell><strong>Make</strong></TableCell>
                            <TableCell><strong>Model</strong></TableCell>
                            <TableCell align="center"><strong>Qty</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {order.items.map((item, itemIdx) => (
                            <TableRow key={itemIdx}>
                              <TableCell>{item.type || '-'}</TableCell>
                              <TableCell>{item.make || '-'}</TableCell>
                              <TableCell sx={{ fontWeight: 500 }}>{item.model || '-'}</TableCell>
                              <TableCell align="center">{item.qty || 1}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      Equipment details not available for this order
                    </Typography>
                  )}
                  {order.instruction && (
                    <Box sx={{ mt: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        <strong>Instructions:</strong> {order.instruction}
                      </Typography>
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            ))}
          </Paper>
        )}

        <Divider sx={{ mb: 2 }} />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <Spinner />
          </Box>
        ) : !showReview ? (
          <>
            {/* Equipment Table */}
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShoppingCartIcon fontSize="small" />
              Select Equipment to Order
            </Typography>
            
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell width="5%" align="center"></TableCell>
                    <TableCell width="16%"><strong>Type</strong></TableCell>
                    <TableCell width="14%"><strong>Make / Supplier</strong></TableCell>
                    <TableCell width="20%"><strong>Model</strong></TableCell>
                    <TableCell width="10%" align="center"><strong>Qty</strong></TableCell>
                    <TableCell width="35%"><strong>Supplier</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orderItems.length > 0 ? (
                    orderItems.map((item) => (
                      <Fragment key={item.id}>
                        <TableRow hover>
                          <TableCell align="center" sx={{ p: 0.5 }}>
                            <IconButton
                              size="small"
                              onClick={() => toggleRowExpanded(item.id)}
                              sx={{ 
                                color: item.instruction ? 'primary.main' : 'grey.400',
                              }}
                            >
                              {expandedRows[item.id] ? (
                                <KeyboardArrowUpIcon fontSize="small" />
                              ) : item.instruction ? (
                                <NotesIcon fontSize="small" />
                              ) : (
                                <KeyboardArrowDownIcon fontSize="small" />
                              )}
                            </IconButton>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{item.type}</Typography>
                            {item.isSupply && (
                              <Typography variant="caption" sx={{ color: 'info.main', display: 'block', lineHeight: 1.2 }}>
                                auto-supply
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{item.make}</Typography>
                            {catalogSuppliers[item.model] && (
                              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.2 }}>
                                {catalogSuppliers[item.model]}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {item.model}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <TextField
                              type="number"
                              size="small"
                              value={item.qty}
                              onChange={(e) => handleQtyChange(item.id, e.target.value)}
                              inputProps={{ min: 0, style: { textAlign: 'center', width: 50 } }}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <FormControl fullWidth size="small">
                              <Select
                                value={item.supplierId || ''}
                                onChange={(e) => handleSupplierChange(item.id, e.target.value)}
                                displayEmpty
                              >
                                <MenuItem value="">
                                  <em>— Select Supplier —</em>
                                </MenuItem>
                                {suppliers.map((supplier) => (
                                  <MenuItem key={supplier.id} value={supplier.id}>
                                    {supplier.name}
                                  </MenuItem>
                                ))}
                                <MenuItem value="other">
                                  <em>Other (custom email)</em>
                                </MenuItem>
                              </Select>
                            </FormControl>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={6} sx={{ p: 0, borderBottom: expandedRows[item.id] ? '1px solid rgba(224, 224, 224, 1)' : 'none' }}>
                            <Collapse in={expandedRows[item.id]} timeout="auto" unmountOnExit>
                              <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                                <TextField
                                  label="Special Instructions for this item"
                                  placeholder="e.g., Need plastic drain pan for condenser..."
                                  fullWidth
                                  size="small"
                                  multiline
                                  rows={2}
                                  value={item.instruction || ''}
                                  onChange={(e) => handleItemInstructionChange(item.id, e.target.value)}
                                  InputProps={{
                                    startAdornment: <NoteAddIcon sx={{ mr: 1, color: 'grey.500' }} />
                                  }}
                                />
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </Fragment>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography color="text.secondary" sx={{ py: 2 }}>
                          No equipment in this proposal
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Custom email for "Other" supplier */}
            {selectedSuppliers.some(s => s.id === 'other') && (
              <Box sx={{ mb: 3 }}>
                <TextField
                  label="Email addresses for 'Other' supplier (comma separated)"
                  fullWidth
                  size="small"
                  value={customEmails}
                  onChange={(e) => setCustomEmails(e.target.value)}
                  placeholder="email1@example.com, email2@example.com"
                />
              </Box>
            )}

            {/* Supplier / catalog mismatch warning */}
            {supplierMismatches.length > 0 && (
              <Alert
                severity="warning"
                icon={<WarningAmberIcon />}
                sx={{ mb: 2 }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Supplier may not carry the selected equipment
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  The chosen supplier does not match the recommended one from the catalog.
                  The item may not be available there.
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {supplierMismatches.map((m) => (
                    <li key={m.id}>
                      <Typography variant="body2">
                        <strong>{m.model}</strong>{m.make && m.make !== '-' ? ` (${m.make})` : ''}: selected{' '}
                        <strong>{m.selected}</strong>, recommended: {m.recommended}
                      </Typography>
                    </li>
                  ))}
                </Box>
              </Alert>
            )}

            {/* Action buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
              <Button variant="outlined" onClick={handleClose}>
                Cancel
              </Button>
              <Button 
                variant="contained" 
                color="primary"
                startIcon={<PreviewIcon />}
                onClick={handleReviewOrder}
                disabled={!isOrderValid}
              >
                Review Order
              </Button>
            </Box>

            {!isOrderValid && orderItems.some(i => i.supplierId) && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {selectedSuppliers.some(s => s.id === 'other') && !customEmails.trim()
                  ? 'Please enter email addresses for "Other" supplier'
                  : 'Please select at least one item with quantity and supplier'
                }
              </Alert>
            )}
          </>
        ) : !loading && showReview ? (
          <>
            <Box sx={{ mb: 2 }}>
              <Button 
                startIcon={<ArrowBackIcon />} 
                onClick={handleBackFromReview}
                sx={{ mb: 2 }}
              >
                Back to Edit
              </Button>
              
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <EmailIcon color="primary" />
                Review & Edit Email Messages
              </Typography>
              
              <Alert severity="info" sx={{ mb: 2 }}>
                Review the email content below. You can edit the text for each supplier before sending.
              </Alert>
            </Box>

            {selectedSuppliers.map((supplier) => {
              // Support new emails array format and legacy single email
              let supplierEmails = [];
              if (supplier.id === 'other') {
                supplierEmails = customEmails ? customEmails.split(',').map(e => e.trim()).filter(Boolean) : [];
              } else if (supplier.emails && Array.isArray(supplier.emails) && supplier.emails.length > 0) {
                supplierEmails = supplier.emails;
              } else if (supplier.email) {
                supplierEmails = [supplier.email];
              }
              
              return (
                <Paper 
                  key={supplier.id} 
                  variant="outlined" 
                  sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {supplier.name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {supplierEmails.length > 0 ? (
                        supplierEmails.map((email, idx) => (
                          <Chip 
                            key={idx}
                            label={email} 
                            size="small" 
                            color="primary"
                            variant="outlined"
                          />
                        ))
                      ) : (
                        <Chip 
                          label="No email" 
                          size="small" 
                          color="error"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </Box>
                  
                  <TextField
                    fullWidth
                    multiline
                    rows={12}
                    value={emailTexts[supplier.id] || ''}
                    onChange={(e) => handleEmailTextChange(supplier.id, e.target.value)}
                    variant="outlined"
                    sx={{
                      '& .MuiInputBase-root': {
                        fontFamily: 'monospace',
                        fontSize: '0.9rem',
                        bgcolor: 'white'
                      }
                    }}
                  />
                </Paper>
              );
            })}

            {/* Send buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
              <Button variant="outlined" onClick={handleBackFromReview}>
                Back
              </Button>
              <Button 
                variant="contained" 
                color="success"
                startIcon={sending ? null : <SendIcon />}
                onClick={handlePlaceOrder}
                disabled={sending}
                size="large"
              >
                {sending ? 'Sending...' : 'Place Order & Send Email'}
              </Button>
            </Box>
          </>
        ) : null}

        {/* Supplier mismatch confirmation */}
        <Dialog
          open={mismatchDialogOpen}
          onClose={() => setMismatchDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'warning.main' }}>
            <WarningAmberIcon color="warning" />
            Supplier mismatch — confirm order
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>
              The supplier you selected is different from the one recommended in the catalog.
              This equipment may not be available there. Please double-check before ordering.
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {supplierMismatches.map((m) => (
                <li key={m.id}>
                  <Typography variant="body2">
                    <strong>{m.model}</strong>{m.make && m.make !== '-' ? ` (${m.make})` : ''}: you selected{' '}
                    <strong>{m.selected}</strong>, recommended: <strong>{m.recommended}</strong>
                  </Typography>
                </li>
              ))}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setMismatchDialogOpen(false)} variant="outlined">
              Go Back & Review
            </Button>
            <Button onClick={handleConfirmMismatch} variant="contained" color="warning">
              Order Anyway
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Modal>
  );
};

export default OrderEquipmentModal;
